// Setup i18n condiviso. react-i18next + browser-language-detector.
// Le risorse vengono caricate sincrone all'avvio (non serve lazy loading:
// con 2 lingue e ~1k chiavi il payload e' minuscolo).
//
// Detector ordine: localStorage "eb.lang" -> navigator.language -> fallback "it".
// Salvataggio scelta: localStorage. Se l'utente e' loggato anche
// auth.users.raw_user_meta_data.lang per sincronizzazione cross-device
// (vedi LangSwitcher).

import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import itCommon from "../locales/it/common.json";
import enCommon from "../locales/en/common.json";
import itAuth from "../locales/it/auth.json";
import enAuth from "../locales/en/auth.json";
import itShell from "../locales/it/shell.json";
import enShell from "../locales/en/shell.json";
import itHome from "../locales/it/home.json";
import enHome from "../locales/en/home.json";
import itSessions from "../locales/it/sessions.json";
import enSessions from "../locales/en/sessions.json";
import itPlayers from "../locales/it/players.json";
import enPlayers from "../locales/en/players.json";
import itGuests from "../locales/it/guests.json";
import enGuests from "../locales/en/guests.json";
import itMondofc from "../locales/it/mondofc.json";
import enMondofc from "../locales/en/mondofc.json";
import itPlayerArea from "../locales/it/playerArea.json";
import enPlayerArea from "../locales/en/playerArea.json";
import itRequests from "../locales/it/requests.json";
import enRequests from "../locales/en/requests.json";
import itSettings from "../locales/it/settings.json";
import enSettings from "../locales/en/settings.json";
import itAccount from "../locales/it/account.json";
import enAccount from "../locales/en/account.json";
import itPublic from "../locales/it/public.json";
import enPublic from "../locales/en/public.json";
import itStats from "../locales/it/stats.json";
import enStats from "../locales/en/stats.json";
import deCommon from "../locales/de/common.json";
import deAuth from "../locales/de/auth.json";
import deShell from "../locales/de/shell.json";
import deHome from "../locales/de/home.json";
import deSessions from "../locales/de/sessions.json";
import dePlayers from "../locales/de/players.json";
import deGuests from "../locales/de/guests.json";
import deMondofc from "../locales/de/mondofc.json";
import dePlayerArea from "../locales/de/playerArea.json";
import deRequests from "../locales/de/requests.json";
import deSettings from "../locales/de/settings.json";
import deAccount from "../locales/de/account.json";
import dePublic from "../locales/de/public.json";
import deStats from "../locales/de/stats.json";
import itSupport from "../locales/it/support.json";
import enSupport from "../locales/en/support.json";
import deSupport from "../locales/de/support.json";
import itSeasons from "../locales/it/seasons.json";
import enSeasons from "../locales/en/seasons.json";
import deSeasons from "../locales/de/seasons.json";

export const SUPPORTED_LANGS = ["it", "en", "de"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

export const LANG_LABELS: Record<SupportedLang, string> = {
  it: "Italiano",
  en: "English",
  de: "Deutsch",
};

export const DEFAULT_LANG: SupportedLang = "it";

export function isSupportedLang(value: unknown): value is SupportedLang {
  return typeof value === "string" && (SUPPORTED_LANGS as readonly string[]).includes(value);
}

const NAMESPACES = [
  "common",
  "auth",
  "shell",
  "home",
  "sessions",
  "players",
  "guests",
  "mondofc",
  "playerArea",
  "requests",
  "settings",
  "account",
  "public",
  "stats",
  "support",
  "seasons",
] as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LANG,
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    ns: NAMESPACES as unknown as string[],
    defaultNS: "common",
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: "eb.lang",
      caches: ["localStorage"],
    },
    resources: {
      it: {
        common: itCommon,
        auth: itAuth,
        shell: itShell,
        home: itHome,
        sessions: itSessions,
        players: itPlayers,
        guests: itGuests,
        mondofc: itMondofc,
        playerArea: itPlayerArea,
        requests: itRequests,
        settings: itSettings,
        account: itAccount,
        public: itPublic,
        stats: itStats,
        support: itSupport,
        seasons: itSeasons,
      },
      en: {
        common: enCommon,
        auth: enAuth,
        shell: enShell,
        home: enHome,
        sessions: enSessions,
        players: enPlayers,
        guests: enGuests,
        mondofc: enMondofc,
        playerArea: enPlayerArea,
        requests: enRequests,
        settings: enSettings,
        account: enAccount,
        public: enPublic,
        stats: enStats,
        support: enSupport,
        seasons: enSeasons,
      },
      de: {
        common: deCommon,
        auth: deAuth,
        shell: deShell,
        home: deHome,
        sessions: deSessions,
        players: dePlayers,
        guests: deGuests,
        mondofc: deMondofc,
        playerArea: dePlayerArea,
        requests: deRequests,
        settings: deSettings,
        account: deAccount,
        public: dePublic,
        stats: deStats,
        support: deSupport,
        seasons: deSeasons,
      },
    },
    returnEmptyString: false,
  });

// Sincronizza l'attributo <html lang="…"> con la lingua corrente. Importante
// per a11y (screen reader) e per <html lang> visibile a strumenti esterni.
// Lo facciamo qui (non in un useEffect React) cosi' parte al primo paint e
// reagisce a ogni cambio lingua, anche quando il LangSwitcher viene chiamato.
function syncHtmlLang(lang: string) {
  if (typeof document === "undefined") return;
  const short = (lang || DEFAULT_LANG).toLowerCase().split("-")[0];
  document.documentElement.lang =
    short === "en" ? "en" : short === "de" ? "de" : "it";
}
syncHtmlLang(i18n.language);
i18n.on("languageChanged", syncHtmlLang);

export default i18n;

// Helper per leggere il locale corrente come BCP-47 (es. 'it-IT', 'en-GB', 'de-DE').
// Usato da Intl.DateTimeFormat/NumberFormat per coerenza con la UI.
export function currentIntlLocale(): string {
  const lang = (i18n.language || DEFAULT_LANG).toLowerCase().split("-")[0];
  if (lang === "en") return "en-GB";
  if (lang === "de") return "de-DE";
  return "it-IT";
}
